import React, { useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";

import "./AnalysisConfidence.scss";
import PageLayout from "../../_layout/PageLayout";
import { DataSection, SubSection } from "../../../components/data-components/sections/DataSections";
import { NumberSelection } from "../../../components/number-selection/NumberSelection";
import Form from "../../../components/form/Form";
import { SettingsMenuItemsModel, SettingsMenuItemsPages } from "../SettingsMenuItems";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { EditOrganizationAnalysisConfidence } from "../../../services/authorization/Permissions";
import { AnalysisConfidenceStorageModel } from "../../../services/organization-analysis-confidence/AnalysisConfidenceStorage";
import { api } from "../../../services/api/ApiService";
import { translationService } from "../../../services/translation/TranslationService";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();
  const [settingsMenuItems] = useState(() =>
    SettingsMenuItemsModel.create({ menuItemPage: SettingsMenuItemsPages.EditAnalysisConfidence })
  );
  const [analysisConfidenceStorage] = useState(() =>
    AnalysisConfidenceStorageModel.create({}, { api })
  );

  const analysisSectionSize = useMemo(
    () => Math.ceil(analysisConfidenceStorage.AnalysisLevels.length / 3),
    [analysisConfidenceStorage.AnalysisLevels.length]
  );

  const highlightSectionSize = useMemo(
    () => Math.ceil(analysisConfidenceStorage.HighlightLevels.length / 3),
    [analysisConfidenceStorage.HighlightLevels.length]
  );

  const goToViewPage = useCallback(() => {
    history.push("/settings/analysis-confidence");
  }, [history]);

  const initialValues = useMemo(() => ({}), []);

  const handleSubmit = useCallback(
    async (values: any) => {
      const output = {
        AnalysisLevels: analysisConfidenceStorage.AnalysisLevels.map((l) => ({
          name: l.name,
          level: values[l.name] ?? l.level,
        })),
        HighlightLevels: analysisConfidenceStorage.HighlightLevels.map((l) => ({
          name: l.name,
          level: values[l.name] ?? l.level,
        })),
      };

      try {
        await analysisConfidenceStorage.updateConfidenceLevels(output);
        return goToViewPage();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.edit");
        handleErrorInSnackbar(applicationError);
      }
    },
    [analysisConfidenceStorage, goToViewPage, handleErrorInSnackbar]
  );

  const translateName = useCallback(
    (name: string): string =>
      translationService.translate(
        `damages.highlight-detection.${name.toLowerCase()}`
      ),
    []
  );

  const mapToFormField = useCallback(
    (
      name: string,
      value: number,
      setFieldValue: any,
      translationName: string = name,
      step: number = 1
    ) => (
      <NumberSelection
        key={name}
        initialValue={value}
        minValue={65}
        maxValue={100}
        labelTranslationKey={"general.add-percentage-at-end"}
        labelTranslationContext={{ text: translationName }}
        callback={(newValue) => setFieldValue(name, newValue)}
        step={step}
      />
    ),
    []
  );

  return (
    <PageLayout
      id="analysis-confidence-edit"
      titleKey="general.analysis-confidence"
      accessWithOneOfThePermissions={[EditOrganizationAnalysisConfidence]}
      isLoading={analysisConfidenceStorage.isLoading}
      menuItems={{
        staticItems: settingsMenuItems.getStaticItems(),
      }}
    >
      <Form
        formikOptions={{
          initialValues: initialValues,
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          enableReinitialize: true,
        }}
        canSubmit={permissionsService.userHasPermissionTo(EditOrganizationAnalysisConfidence)}
        onCancel={goToViewPage}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="ViewAnalysisConfidence.analysis.section-title">
                <SubSection className="confidence-section">
                  {analysisConfidenceStorage.AnalysisLevels.slice(
                    undefined,
                    analysisSectionSize
                  ).map((x) => mapToFormField(x.name, x.level, setFieldValue))}
                </SubSection>
                <SubSection className="confidence-section">
                  {analysisConfidenceStorage.AnalysisLevels.slice(
                    analysisSectionSize,
                    analysisSectionSize * 2
                  ).map((x) => mapToFormField(x.name, x.level, setFieldValue))}
                </SubSection>
                <SubSection className="confidence-section">
                  {analysisConfidenceStorage.AnalysisLevels.slice(analysisSectionSize * 2).map(
                    (x) => mapToFormField(x.name, x.level, setFieldValue)
                  )}
                </SubSection>
              </DataSection>
              <DataSection labelKey="ViewAnalysisConfidence.highlight.section-title">
                <SubSection className="confidence-section">
                  {analysisConfidenceStorage.HighlightLevels.slice(
                    undefined,
                    highlightSectionSize
                  ).map((x) =>
                    mapToFormField(x.name, x.level, setFieldValue, translateName(x.name), 5)
                  )}
                </SubSection>
                <SubSection className="confidence-section">
                  {analysisConfidenceStorage.HighlightLevels.slice(
                    highlightSectionSize,
                    highlightSectionSize * 2
                  ).map((x) =>
                    mapToFormField(x.name, x.level, setFieldValue, translateName(x.name), 5)
                  )}
                </SubSection>
                <SubSection className="confidence-section">
                  {analysisConfidenceStorage.HighlightLevels.slice(highlightSectionSize * 2).map(
                    (x) => mapToFormField(x.name, x.level, setFieldValue, translateName(x.name), 5)
                  )}
                </SubSection>
              </DataSection>
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
